import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { whatsappApi } from '../../utils/api';
import QRCode from 'qrcode.react';
import { supabase } from '../../utils/supabase';
import { Form, Input, Button, message } from 'antd';

const WhatsAppConnection = () => {
  const [error, setError] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  // Get WhatsApp connection status
  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['whatsapp-status'],
    queryFn: async () => {
      const { data } = await whatsappApi.getStatus();
      return data;
    },
    refetchInterval: 5000 // Poll every 5 seconds
  });

  // Get QR code if not connected
  const { data: qrCode, isLoading: qrLoading } = useQuery({
    queryKey: ['whatsapp-qr'],
    queryFn: async () => {
      const { data } = await whatsappApi.getQR();
      return data.qr;
    },
    enabled: status?.status === 'WAITING_FOR_SCAN',
    refetchInterval: status?.status === 'WAITING_FOR_SCAN' ? 5000 : false,
    retry: false, // Don't retry on error
    onError: () => {} // Silence errors as 404 is expected when QR is not available
  });

  // Connect mutation
  const connectMutation = useMutation({
    mutationFn: whatsappApi.connect,
    onSuccess: () => {
      queryClient.invalidateQueries(['whatsapp-status']);
      setError(null);
    },
    onError: (err) => {
      setError(err.message);
      console.error('Connection error:', err);
    }
  });

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: whatsappApi.disconnect,
    onSuccess: () => {
      queryClient.invalidateQueries(['whatsapp-status']);
      queryClient.invalidateQueries(['whatsapp-qr']);
      setError(null);
    },
    onError: (err) => {
      setError(err.message);
      console.error('Disconnection error:', err);
    }
  });

  const loadSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: settings, error } = await supabase
        .from('settings')
        .select('admin_phone, admin_phone2')
        .eq('user_id', user.id)
        .eq('type', 'whatsapp')
        .single();

      if (error) throw error;
      
      if (settings) {
        form.setFieldsValue({
          admin_phone: settings.admin_phone,
          admin_phone2: settings.admin_phone2
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const saveAdminPhone = async (values) => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Format du numéro de téléphone
      let formattedPhone = values.admin_phone.replace(/\D/g, '');
      let formattedPhone2 = values.admin_phone2?.replace(/\D/g, '') || null;

      const { error } = await supabase
        .from('settings')
        .upsert({
          user_id: user.id,
          type: 'whatsapp',
          admin_phone: formattedPhone,
          admin_phone2: formattedPhone2,
        }, {
          onConflict: 'user_id,type',
        });

      if (error) throw error;

      message.success('Numéros admin sauvegardés avec succès');
    } catch (error) {
      message.error('Erreur: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    connectMutation.mutate();
  };

  const handleDisconnect = () => {
    disconnectMutation.mutate();
  };

  useEffect(() => {
    loadSettings();
  }, []);

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">WhatsApp Connection</h2>
          <span
            className={`px-2 py-1 text-sm font-semibold rounded-full ${
              status?.status === 'CONNECTED'
                ? 'bg-green-100 text-green-800'
                : status?.status === 'WAITING_FOR_SCAN'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {status?.status || 'DISCONNECTED'}
          </span>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">
            {error}
          </div>
        )}

        {status?.status === 'WAITING_FOR_SCAN' && qrCode && (
          <div className="flex flex-col items-center space-y-2">
            <p className="text-sm text-gray-600">Scannez ce code QR avec WhatsApp sur votre téléphone</p>
            <div className="flex justify-center p-4 bg-gray-50 rounded-lg">
              <QRCode value={qrCode} size={256} level="H" />
            </div>
          </div>
        )}

        {status?.status === 'CONNECTED' && (
          <div className="mt-4">
            <Form
              form={form}
              onFinish={saveAdminPhone}
              layout="vertical"
            >
              <Form.Item
                name="admin_phone"
                label="Numéro de téléphone admin principal"
                tooltip="Ce numéro pourra envoyer des commandes via WhatsApp"
                rules={[
                  { required: true, message: 'Le numéro est requis' },
                ]}
              >
                <Input placeholder="Ex: 0701020304" />
              </Form.Item>

              <Form.Item
                name="admin_phone2"
                label="Numéro de téléphone admin secondaire"
                tooltip="Ce numéro pourra aussi envoyer des commandes via WhatsApp (optionnel)"
              >
                <Input placeholder="Ex: 0701020304" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  className="w-full"
                >
                  Sauvegarder les numéros admin
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          {status?.status === 'CONNECTED' ? (
            <button
              onClick={handleDisconnect}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              disabled={disconnectMutation.isPending}
            >
              Disconnect
            </button>
          ) : (
            <button
              onClick={handleConnect}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={connectMutation.isPending || status?.status === 'WAITING_FOR_SCAN'}
            >
              {status?.status === 'WAITING_FOR_SCAN' 
                ? 'En cours de connexion...' 
                : connectMutation.isPending 
                  ? 'Connexion...' 
                  : 'Connecter'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppConnection;
